import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { sound } from "../lib/sound";

const ProfileContext = createContext(null);
const STORAGE_KEY = "mathrush_profile_id";
const RM_KEY = "mathrush_reduce_motion";
const READ_KEY = "mathrush_readaloud";

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem(RM_KEY) === "1");
  const [soundMuted, setSoundMutedState] = useState(() => localStorage.getItem("mathrush_muted") === "1");
  const [readAloud, setReadAloudState] = useState(() => localStorage.getItem(READ_KEY) === "1");

  const refresh = useCallback(async () => {
    const id = localStorage.getItem(STORAGE_KEY);
    if (!id) { setProfile(null); setLoading(false); return; }
    try {
      const p = await api.getProfile(id);
      setProfile(p);
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    document.documentElement.classList.toggle("reduce-motion", reduceMotion);
    localStorage.setItem(RM_KEY, reduceMotion ? "1" : "0");
  }, [reduceMotion]);

  const setSoundMuted = useCallback((m) => {
    setSoundMutedState(m);
    sound.setMuted(m);
  }, []);

  const setReadAloud = useCallback((v) => {
    setReadAloudState(v);
    localStorage.setItem(READ_KEY, v ? "1" : "0");
  }, []);

  const selectProfile = useCallback((p) => {
    localStorage.setItem(STORAGE_KEY, p.id);
    setProfile(p);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setProfile(null);
  }, []);

  return (
    <ProfileContext.Provider
      value={{ profile, loading, refresh, selectProfile, logout, reduceMotion, setReduceMotion,
               soundMuted, setSoundMuted, readAloud, setReadAloud }}>
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfile = () => useContext(ProfileContext);
